<template>
    <div
        v-if="isLoaded && !apis.length"
        class="box-body with-border clearfix"
    >
        <p>
            {{ $t('messages.api.noApis') }}
            <a href="/apis/new">
                {{ $t('addNew') }}
            </a>
        </p>
    </div>

    <div v-else>
        <div
            v-if="isLoaded && paginationOptions.total"
            class="box-body with-border clearfix"
        >
            <BadgeTotal
                :value="paginationOptions.total"
                color="gray"
                :label="`${$t('total')}: `"
            />

            <div
                v-if="paginationOptions.last_page > 1"
                class="pagination-box"
            >
                <PaginationTable
                    :options="paginationOptions"
                    @change-page="fetchApis"
                />
            </div>
        </div>

        <div class="box-body table-responsive no-padding">
            <div
                v-if="isLoading"
                class="box-body with-border clearfix"
            >
                <tr>
                    <td>
                        <img
                            class="table-row-preloader"
                            rel="preload"
                            src="/img/table-row-preloader.gif"
                        />
                    </td>
                </tr>
            </div>

            <table
                v-else
                class="table table-striped table-hover"
            >
                <tbody>
                <tr>
                    <th>{{ $t('id') }}</th>

                    <th>{{ $t('name') }}</th>

                    <th v-if="!providerId">{{ $t('provider.provider') }}</th>

                    <th>{{ $t('account.account') }}</th>

                    <th>{{ $t('type') }}</th>

                    <th>{{ $t('message.messages') }}</th>

                    <th>{{ $t('clicks') }}</th>

                    <th class="text-center">{{ $t('campaign.campaigns') }}</th>

                    <th class="text-center">{{ $t('tags') }}</th>

                    <th class="text-center">{{ $t('asnFiles') }}</th>

                    <th class="text-center">{{ $t('asnGroups') }}</th>

                    <th class="text-center">{{ $t('sendingRate') }}</th>

                    <th class="text-center">{{ $t('active') }}</th>

                    <th class="text-center">{{ $t('nonUsaAvailable') }}</th>

                    <th class="text-center">{{ $t('usePlus') }}</th>

                    <th class="text-right">{{ $t('createdAt') }}</th>

                    <th class="text-right">{{ $t('updatedAt') }}</th>

                    <th></th>
                </tr>

                <tr
                    v-for="api in apis"
                    :key="api.id"
                >
                    <td>{{ api.id }}</td>

                    <td v-if="api.id === apiDefaultId">
                        <strong
                            :title="$t('defaultApi')"
                            class="text-primary"
                        >
                            {{ api.name }}
                        </strong>
                    </td>

                    <td v-else>
                        {{ api.name }}
                    </td>

                    <td v-if="!providerId">
                        <a
                            v-if="api.provider"
                            :href="`/apis/providers/${api.provider.id}`"
                        >
                                <span
                                    :class="{
                                        'text-red': !api.provider.active,
                                    }"
                                    :title="!api.provider.active ? $t('inactive') : undefined"
                                >
                                    {{ api.provider.name }}
                                </span>
                        </a>
                    </td>

                    <td>
                        <a
                            v-if="api.account"
                            :href="`/apis/accounts/${api.account.id}`"
                        >
                                <span>
                                    {{ api.account.name }}
                                </span>
                        </a>
                    </td>

                    <td>{{ campaignTypes[api.campaign_type_id] }}</td>

                    <td>
                        <img
                            v-show="isLoadingStats"
                            class="table-row-preloader"
                            rel="preload"
                            src="/img/table-row-preloader.gif"
                        />

                        <BadgeTotal
                            v-show="!isLoadingStats"
                            :title="$t('total')"
                            :value="api.messages_count"
                            color="gray"
                        />

                        <BadgeTotal
                            v-show="!isLoadingStats"
                            :title="$t('sent')"
                            :value="api.messages_sent"
                            color="green"
                        />

                        <BadgeTotal
                            v-show="!isLoadingStats"
                            :title="$t('failed')"
                            :value="api.messages_failed"
                            color="red"
                        />

                        <BadgeTotal
                            v-show="!isLoadingStats"
                            :title="$t('blacklisted')"
                            :value="api.messages_blacklisted"
                            color="black"
                        />
                    </td>

                    <td>
                        <img
                            v-show="isLoadingStats"
                            class="table-row-preloader"
                            rel="preload"
                            src="/img/table-row-preloader.gif"
                        />

                        <BadgeTotal
                            v-show="!isLoadingStats"
                            :title="$t('percentage')"
                            :value="api.percentage + '%'"
                            color="light-blue"
                        />

                        <BadgeTotal
                            v-show="!isLoadingStats"
                            :title="$t('amount')"
                            :value="api.clicks_count"
                            color="aqua-active"
                        />
                    </td>

                    <td class="text-center">
                        <BadgeTotal
                            :title="$t('campaign.campaignsCount')"
                            :value="api.campaigns_count"
                            color="gray"
                        />
                    </td>

                    <td class="text-center">
                        <BadgeTotal
                            :title="$t('tag.tagsCount')"
                            :value="api.tags_count || 0"
                            color="gray"
                        />
                    </td>

                    <td class="text-center">
                        <BadgeTotal
                            :title="$t('total')"
                            :value="api.source_number_files_count"
                            color="gray"
                        />
                    </td>

                    <td class="text-center">
                        <BadgeTotal
                            :title="$t('total')"
                            :value="api.buy_groups_count"
                            color="gray"
                        />
                    </td>

                    <td class="text-center">
                        <BadgeTotal
                            v-if="api.account && api.account.sending_rate"
                            :title="$t('perSecond')"
                            :value="api.account.sending_rate"
                            color="gray"
                        />
                    </td>

                    <td class="text-center">
                        <ValueYesNo :value="api.active"/>
                    </td>

                    <td class="text-center">
                        <ValueYesNo :value="api.is_non_usa"/>
                    </td>
                    <td class="text-center">
                        <ValueYesNo
                            v-if="api.provider.use_setting_plus"
                            :value="Boolean(api.use_plus)"
                        />
                        <em v-else class="text-info">{{ $t('none') }}</em>
                    </td>

                    <td class="text-right">
                        {{ formatDate(api.created_at) }}
                    </td>

                    <td class="text-right">
                        {{ formatDate(api.updated_at) }}
                    </td>

                    <td class="text-right text-nowrap">
                        <a
                            :href="`/apis/${api.id}`"
                            :title="$t('show')"
                            class="btn btn-sm btn-info"
                        >
                            <i class="fa fa-eye"></i>
                            <span class="sr-only">{{ $t('show') }}</span>
                        </a>

                        <a
                            :href="`/apis/${api.id}/edit`"
                            :title="$t('edit')"
                            class="btn btn-sm btn-primary"
                        >
                            <i class="fa fa-edit"></i>
                            <span class="sr-only">{{ $t('edit') }}</span>
                        </a>

                        <a
                            :href="`/apis/export-source-numbers/${api.id}`"
                            :title="$t('export.exportApiSourceNumbers')"
                            class="btn btn-sm btn-warning"
                        >
                            <i class="fa fa-download"></i>
                            <span class="sr-only">{{ $t('export.exportApiSourceNumbers') }}</span>
                        </a>
                    </td>
                </tr>
                </tbody>
            </table>
        </div>

        <div
            v-if="paginationOptions.last_page > 1"
            class="box-footer clearfix"
        >
            <div class="pagination-box">
                <PaginationTable
                    :options="paginationOptions"
                    @change-page="fetchApis"
                />
            </div>
        </div>
    </div>
</template>

<script>
import EventBus from '@/event-bus';
import formatsMixin from '@/mixins/formatsMixin';
import PaginationTable from '@/components/subcomponents/PaginationTable';
import BadgeTotal from '@/components/subcomponents/BadgeTotal';
import ValueYesNo from '@/components/subcomponents/ValueYesNo';

export default {
    components: {
        BadgeTotal,
        ValueYesNo,
        PaginationTable,
    },
    mixins: [formatsMixin],
    props: {
        isLoading: {
            type: Boolean,
            default: false,
        },
        providerId: {
            type: Number,
            default: undefined,
        },
        orderBy: {
            type: String,
            default: 'id',
        },
        orderByDirection: {
            type: String,
            default: 'desc',
        },
    },
    data() {
        return {
            isLoaded: false,
            isLoadingStats: false,
            apis: [],
            stats: [],
            accounts: [],
            tags: [],
            apiDefaultId: '',
            ids: [],
            campaignTypes: [],
            paginationOptions: {
                disabled: true,
                total: 0,
            },
        };
    },
    async created() {

        await this.fetchApis(this.$route.query.page);

        this.isLoaded = true;
        this.$emit('loaded', {
            apis: this.apis,
            accounts: this.accounts,
            tags: this.tags,
        });

        EventBus.$on('relodList', () => {
            this.fetchApis(this.$route.query.page);
        });
    },
    methods: {
        async fetchApis(page = 1) {
            try {
                this.$router
                    .replace({
                        query: {
                            ...this.$route.query,
                            page: page > 1 ? page : undefined,
                        },
                    })
                    .catch(() => {});

                this.paginationOptions.disabled = true;

                let filters = {};
                if (this.$route.query['filter[search]']) {
                    filters.search = this.$route.query['filter[search]'];
                }

                if (this.$route.query['filter[sourceNumber]']) {
                    filters.sourceNumber = this.$route.query['filter[sourceNumber]'];
                }

                if (this.$route.query['filter[accountId]']) {
                    filters.accountId = this.$route.query['filter[accountId]'];
                }

                if (this.$route.query['filter[tags]']) {
                    filters.tags = Array.isArray(this.$route.query['filter[tags]'])
                        ? this.$route.query['filter[tags]']
                        : [this.$route.query['filter[tags]']];
                }

                const {
                    data: {
                        apis: { data: apis, ...paginationOptions },
                        accounts,
                        tags,
                        apiDefaultId,
                        campaignTypes,
                        ids,
                    },
                } = await axios.get(this.providerId ? `/apis/providers/${this.providerId}/data-api` : '/apis/data', {
                    params: {
                        filters: {
                            ...filters,
                        },
                        page,
                        orderBy: this.orderBy,
                        orderByDirection: this.orderByDirection,
                    },
                });

                this.apis = apis;
                this.paginationOptions = paginationOptions;
                this.accounts = accounts;
                this.tags = tags;
                this.apiDefaultId = apiDefaultId;
                this.campaignTypes = campaignTypes;
                this.ids = ids || [];

                if (this.ids.length) {
                    await this.fetchStats(this.ids);
                }
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.paginationOptions.disabled = false;
            }
        },
        async fetchStats(ids) {
            try {
                this.isLoadingStats = true;

                const {
                    data: { stats },
                } = await axios.get(this.providerId ? `/apis/providers/${this.providerId}/stats-api` : '/apis/data-stats', {
                    params: {
                        ids: ids.join(','),
                    },
                });

                this.apis = this.apis.map((api) => {
                    const stat = stats.find((stat) => stat.id === api.id);

                    if (stat) {
                        api = {
                            ...api,
                            ...stat,
                        };
                    }

                    return api;
                });
            } catch (error) {
                EventBus.$emit('alertData', {
                    class: 'alert-danger',
                    message: error.response.data.message,
                    errors: error.response.data.errors,
                });
            } finally {
                this.isLoadingStats = false;
            }
        },
    },
};
</script>
